import React, { useState, useEffect } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Typography,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import { BsCheckCircle } from "react-icons/bs";
import { HiOutlineDocumentDownload } from "react-icons/hi";
import { getAllSurveyResponsesExportPdf } from "../../../services/surveyResponse.service";
import { getAllProgramsFilters } from "../../../services/program.service";

const ExportButtonDropdown = ({ Exportstatus }) => {
  const [loadingExport, setLoadingExport] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [currentPhaseMessage, setCurrentPhaseMessage] = useState("Initialisation de l'export...");
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const programFilters = await getAllProgramsFilters();
        setPrograms(programFilters);
      } catch (error) {
        console.error("Error fetching programs:", error);
      }
    };

    fetchPrograms();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleExportClick = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const simulateProgress = () => {
    // Reset progress
    setExportProgress(0);
    setCurrentPhaseMessage("Initialisation de l'export...");
    
    // Define phases of export with realistic timing
    // Total time: ~2 minutes (120 seconds)
    const phases = [
      { target: 5, message: "Initialisation de l'export...", duration: 3000 },
      { target: 15, message: "Récupération des données...", duration: 10000 },
      { target: 30, message: "Traitement des candidatures...", duration: 20000 },
      { target: 50, message: "Génération du fichier Excel...", duration: 30000 },
      { target: 75, message: "Formatage des données...", duration: 30000 },
      { target: 95, message: "Finalisation de l'export...", duration: 20000 },
      { target: 99, message: "Préparation du téléchargement...", duration: 7000 }
      // The final 100% will be set when the download actually starts
    ];
    
    let currentPhaseIndex = 0;
    let startTime = Date.now();
    let startProgress = 0;
    
    const interval = setInterval(() => {
      const currentPhase = phases[currentPhaseIndex];
      const elapsedTime = Date.now() - startTime;
      const phaseProgress = Math.min(1, elapsedTime / currentPhase.duration);
      
      // Calculate progress based on current phase
      const targetProgress = startProgress + 
        (currentPhase.target - startProgress) * phaseProgress;
      
      setExportProgress(targetProgress);
      setCurrentPhaseMessage(currentPhase.message);
      
      // Move to next phase if current phase is complete
      if (phaseProgress >= 1 && currentPhaseIndex < phases.length - 1) {
        currentPhaseIndex++;
        startTime = Date.now();
        startProgress = currentPhase.target;
      }
      
      // Stop when all phases are complete
      if (currentPhaseIndex === phases.length - 1 && phaseProgress >= 1) {
        // Keep at 99% until the actual download starts
        setExportProgress(99);
      }
    }, 500);
    
    return interval;
  };



  const handleExportConfirm = async () => {
    if (!selectedProgram) {
      showSnackbar("Veuillez sélectionner un programme", "error");
      return;
    }

    setLoadingExport(true);
    const startTime = Date.now();
    const interval = simulateProgress();
    
    // Start estimated time counter
    const timeInterval = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      const progress = exportProgress;
      
      if (progress > 0) {
        // Estimate total time based on progress so far
        const estimatedTotalSeconds = (elapsedSeconds / progress) * 100;
        const remainingSeconds = Math.max(0, Math.ceil(estimatedTotalSeconds - elapsedSeconds));
        
        // Format remaining time
        if (remainingSeconds > 60) {
          const minutes = Math.floor(remainingSeconds / 60);
          const seconds = remainingSeconds % 60;
          setEstimatedTimeRemaining(`${minutes}m ${seconds}s restant`);
        } else {
          setEstimatedTimeRemaining(`${remainingSeconds}s restant`);
        }
      }
    }, 1000);

    try {
      await getAllSurveyResponsesExportPdf({
        programId: selectedProgram,
        status: Exportstatus || undefined,
      });
      
      // Download completed successfully
      setExportProgress(100);
      setCurrentPhaseMessage("Téléchargement terminé!");
      setExportSuccess(true);
      showSnackbar("Export réussi! Fichier téléchargé.");
      
      // Reset after 3 seconds
      setTimeout(() => {
        setExportSuccess(false);
        setOpenDialog(false);
        setEstimatedTimeRemaining(null);
      }, 3000);
    } catch (error) {
      console.error("Export failed:", error);
      showSnackbar("Échec de l'export. Veuillez réessayer.", "error");
    } finally {
      clearInterval(interval);
      clearInterval(timeInterval);
      setLoadingExport(false);
      setEstimatedTimeRemaining(null);
    }
  };

  const handleProgramSelect = (event) => {
    const programId = event.target.value;
    setSelectedProgram(programId);
  };

  return (
    <>
      <Tooltip title="Exporter les données en Excel">
        <Button
          variant="contained"
          onClick={handleExportClick}
          startIcon={loadingExport ? <CircularProgress size={20} color="inherit" /> : <HiOutlineDocumentDownload size={20} />}
          sx={{
            backgroundColor: "#0976BC",
            color: "white",
            borderRadius: "8px",
            padding: "8px 16px",
            textTransform: "none",
            fontWeight: "medium",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            "&:hover": {
              backgroundColor: "#065A94",
            },
          }}
          disabled={loadingExport}
        >
          {loadingExport ? "Exportation..." : "Exporter en Excel"}
        </Button>
      </Tooltip>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold", pt: 3 }}>
          Exporter les données en Excel
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Sélectionnez un programme pour exporter les candidatures associées.
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="program-select-label">Programme</InputLabel>
            <Select
              labelId="program-select-label"
              value={selectedProgram}
              onChange={handleProgramSelect}
              label="Programme"
              disabled={loadingExport}
            >
              <MenuItem value="" disabled>
                <em>Sélectionnez un programme</em>
              </MenuItem>
              {programs.map((program) => (
                <MenuItem key={program.id} value={program.id}>
                  {program.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loadingExport && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  {currentPhaseMessage}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {Math.round(exportProgress)}%
                </Typography>
              </Box>
              
              {estimatedTimeRemaining && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Temps estimé: {estimatedTimeRemaining}
                </Typography>
              )}
              
              <LinearProgress 
                variant="determinate" 
                value={exportProgress} 
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: exportSuccess ? '#4caf50' : '#0976BC',
                    transition: 'transform 0.5s linear'
                  }
                }}
              />
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                L'exportation peut prendre jusqu'à 2 minutes selon le volume de données
              </Typography>
            </Box>
          )}

          {exportSuccess && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 2 }}>
              <BsCheckCircle color="#4caf50" size={24} style={{ marginRight: 8 }} />
              <Typography variant="body1" color="success.main">
                Export réussi! Téléchargement en cours...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, justifyContent: 'space-between' }}>
          <Button 
            onClick={handleCloseDialog} 
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              borderColor: '#0976BC',
              color: '#0976BC',
              '&:hover': {
                borderColor: '#065A94',
                backgroundColor: 'rgba(9, 118, 188, 0.04)'
              }
            }}
            disabled={loadingExport}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleExportConfirm} 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              backgroundColor: '#0976BC',
              '&:hover': {
                backgroundColor: '#065A94'
              }
            }}
            disabled={!selectedProgram || loadingExport}
            startIcon={loadingExport ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loadingExport ? 'Exportation...' : 'Exporter'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExportButtonDropdown;
